import { MutableRefObject, useCallback, useEffect, useRef, useState, VoidFunctionComponent } from 'react';
import { useDisconnect } from 'wagmi';
import { useKey } from 'react-use';
import { useFrame } from '@react-three/fiber';
import { MeshBasicMaterial } from 'three';
import { Text, useTexture, PerspectiveCamera } from '@react-three/drei';
import { Screen } from '@/components/Screen';
import { PinPad, PINPAD_WIDTH } from '@/components/scenes/code-input/PinPad';
import { garamondNarrowLight, hooge } from '@/lib/fonts';
import { OkCancelMetaball } from '@/components/scenes/code-input/OkCancelMetaball';
import skullPng from '@/img/skull.png';
import { useResponsive } from '@/hooks/useResponsive';
import { buildApiUrl } from '@/lib/globals';
import { useCollectionInfo } from '@/hooks/useCollectionInfo';
import enterSound from '@/sounds/ob-sfx-codebox-digit-input.mp3';
import deleteSound from '@/sounds/ob-sfx-codebox-digit-delete.mp3';
import successfulCodeSound from '@/sounds/ob-sfx-game-start.mp3';
import badCodeSound from '@/sounds/ob-sfx-codebox-fail.mp3';
import { clampedLerp } from '@/lib/mathUtils';
import { useOnlybotsSound } from '@/hooks/useOnlybotsSound';
import { useWalletContext } from '@/context/WalletContext';
import { useRouterContext } from '@/context/RouterContext';

const MAX_CODE_LENGTH = 8;
const SKULL_ASPECT_RATIO = [11, 9.9];
const SHOW_SKULL_TIME = 2;
const SKULL_FADE_IN_TIME = 0.2;
const SKULL_FADE_OUT_TIME = 0.2;
const SKULL_DELAY = 0.1;

interface Props {
    backToTitle: () => void;
    onToStory: () => void;
    straightToConnect: () => void;
}

export const CodeInput: VoidFunctionComponent<Props> = ({ onToStory, backToTitle, straightToConnect }) => {
    const [playEnterSound] = useOnlybotsSound(enterSound);
    const [playDeleteSound] = useOnlybotsSound(deleteSound);
    const [playBadCodeSound] = useOnlybotsSound(badCodeSound);
    const [playSuccessfulCodeSound] = useOnlybotsSound(successfulCodeSound);
    const { responsiveValue } = useResponsive();
    const { isValidBot } = useCollectionInfo();
    const { navigate } = useRouterContext();
    const [code, setCode] = useState<string>('');
    const { scale } = useResponsive();
    const [failedCode, setFailedCode] = useState<boolean>(false);
    const skull = useTexture(skullPng);
    const skullRef = useRef() as MutableRefObject<MeshBasicMaterial>;
    const failedCodeTimer = useRef<number>(0);
    const { disconnect } = useDisconnect();
    const { setSignature, setSignedAddress, setCode: persistCode } = useWalletContext();

    const clearState = useCallback(() => {
        window.localStorage.clear();
        setSignature(null);
        setSignedAddress(null);
        persistCode(null);
        disconnect();
        onToStory();
    }, [disconnect, onToStory, persistCode, setSignature, setSignedAddress]);

    useEffect(() => {
        if (failedCode) {
            playBadCodeSound();
        }
    }, [failedCode, playBadCodeSound]);

    const verifyCode = useCallback(async () => {
        try {
            const response = await fetch(buildApiUrl('validate-code'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                mode: 'cors',
                body: JSON.stringify({
                    code,
                }),
            });
            const status = response.status;
            if (status === 401) {
                setFailedCode(true);
                return;
            }
            const { resetState, bypassQuiz } = await response.json();
            if (resetState) {
                playSuccessfulCodeSound();
                clearState();
            } else if (bypassQuiz) {
                clearState();
                playSuccessfulCodeSound();
                persistCode(code);
                straightToConnect();
            } else {
                setFailedCode(true);
            }
        } catch (e) {
            if (process.env.NODE_ENV === 'development') {
                console.warn(
                    'For local development, you must have onlybots-functions running to get a recaptcha score.'
                );
            }
            console.error(e);
            setFailedCode(true);
        }
    }, [clearState, code, persistCode, playSuccessfulCodeSound, straightToConnect]);

    const submitCode = useCallback(() => {
        if (code.length > 0) {
            if (code[0] === '@') {
                void verifyCode();
                return;
            } else if (isValidBot(code)) {
                const tokenId = parseInt(code, 10).toString();
                playSuccessfulCodeSound();
                navigate(tokenId);
                return;
            }
        }
        setFailedCode(true);
    }, [code, isValidBot, navigate, playSuccessfulCodeSound, verifyCode]);

    const keyPress = useCallback(
        (key: string) => {
            if (key === 'del') {
                setCode((code) => code.slice(0, -1));
                playDeleteSound();
            } else {
                setCode((code) => (code.length < MAX_CODE_LENGTH ? `${code}${key}` : code));
                playEnterSound();
            }
        },
        [playDeleteSound, playEnterSound]
    );

    useKey('Enter', submitCode, undefined, [code]);

    const reset = useCallback(() => {
        setFailedCode(false);
        setCode('');
    }, []);

    useFrame((_, delta) => {
        if (failedCode) {
            failedCodeTimer.current += delta;
            if (failedCodeTimer.current < SKULL_FADE_IN_TIME + SKULL_DELAY) {
                skullRef.current.opacity = clampedLerp(
                    0,
                    1,
                    (failedCodeTimer.current - SKULL_DELAY) / SKULL_FADE_IN_TIME
                );
            } else if (failedCodeTimer.current > SHOW_SKULL_TIME - SKULL_FADE_OUT_TIME - SKULL_DELAY) {
                skullRef.current.opacity = clampedLerp(
                    1,
                    0,
                    (failedCodeTimer.current - (SHOW_SKULL_TIME - SKULL_FADE_OUT_TIME - SKULL_DELAY)) /
                        SKULL_FADE_OUT_TIME
                );
            }
            if (failedCodeTimer.current > SHOW_SKULL_TIME) {
                failedCodeTimer.current = 0;
                reset();
            }
        }
    });

    if (failedCode) {
        return (
            <>
                <PerspectiveCamera position={[0, 0, 5]} far={1000} fov={75} makeDefault />
                <mesh scale={scale}>
                    <planeGeometry
                        attach="geometry"
                        args={responsiveValue(
                            [SKULL_ASPECT_RATIO[0] * 0.5, SKULL_ASPECT_RATIO[1] * 0.5],
                            [SKULL_ASPECT_RATIO[0] * 0.35, SKULL_ASPECT_RATIO[1] * 0.35]
                        )}
                    />
                    <meshBasicMaterial ref={skullRef} color="red" map={skull} transparent opacity={0} />
                </mesh>
            </>
        );
    }
    return (
        <Screen>
            <group position={[0, 2.57, 0]}>
                {code ? (
                    <Text
                        position={[0, 0.085, 0]}
                        font={hooge}
                        fontSize={0.4}
                        color="#85ABFF"
                        anchorX="center"
                        fillOpacity={1.0}
                    >
                        {code}
                    </Text>
                ) : (
                    <Text font={garamondNarrowLight} fontSize={0.35} color="white" anchorX="center" fillOpacity={1.0}>
                        Enter code to continue
                    </Text>
                )}
            </group>
            <group position={[0, 0.11, 0]}>
                <PinPad onKeyPress={keyPress} />
            </group>
            <group position={[-PINPAD_WIDTH / 2, -2.43, 0]}>
                <OkCancelMetaball onOk={submitCode} onCancel={() => (code.length > 0 ? setCode('') : backToTitle())} />
            </group>
        </Screen>
    );
};
